import { SharedStyles } from '@opencare/shared-styles';
import { IconCheck } from '@tabler/icons-react';
import styled, { css } from 'styled-components';

import GiftIconImage from 'assets/icons/gift-icon.png';
import { TYPOGRAPHY } from 'brand-guidelines/typography';
import { applyStylesForScreenSize, SCREEN_SIZES } from 'brand-guidelines/utils/screenSizeUtils';
import { colors } from 'common/styles/style-constants';
import { Modal } from 'opencare-crown';

const tabletAndUp = [SCREEN_SIZES.TABLET, SCREEN_SIZES.SMALL_DESKTOP, SCREEN_SIZES.DESKTOP];

export const StyledModal = styled(Modal)`
  && {
    .ant-modal-content {
      background: transparent;
      box-shadow: none;
      border-radius: 16px;
      overflow: hidden;
    }

    .ant-modal-body {
      padding: 0;
    }

    .ant-modal-close {
      top: 28px;
      right: 16px;
      transform: translateY(-50%);
      color: ${colors.white};

      ${applyStylesForScreenSize(tabletAndUp, css`
        top: 32px;
      `)}

      &:hover {
        color: ${colors.white};
      }
    }
  }
`;

export const ModalHeader = styled.div`
  background: ${SharedStyles.GRADIENT.CORAL};
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;

  ${applyStylesForScreenSize(tabletAndUp, css`
    justify-content: center;
    padding: 20px 32px;
  `)}
`;

export const GiftIcon = styled.img.attrs({ src: GiftIconImage, alt: '' })`
  width: 24px;
  height: 24px;
`;

export const HeaderTitle = styled.h2`
  ${TYPOGRAPHY.CIRCULAR.bold};
  color: ${colors.white};
  font-size: 16px;
  margin: 0;
  line-height: 1.3;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 40px;

  ${applyStylesForScreenSize(tabletAndUp, css`
    font-size: 18px;
    padding-right: 0;
  `)}
`;

export const ModalContent = styled.div`
  background: ${colors.white};
  border-radius: 0 0 16px 16px;
  padding: 24px 20px;

  ${applyStylesForScreenSize(tabletAndUp, css`
    padding: 32px 40px;
  `)}
`;

export const ContentCard = styled.div`
  padding: 0 0 24px 0;
  margin-bottom: 16px;

  ${applyStylesForScreenSize(tabletAndUp, css`
    padding: 0 0 32px 0;
  `)}
`;

export const SectionTitle = styled.h3`
  ${TYPOGRAPHY.CIRCULAR.black};
  color: ${colors.blue};
  font-size: 24px;
  line-height: 32px;
  margin: 0 0 24px 0;
  text-align: left;

  ${applyStylesForScreenSize(tabletAndUp, css`
    text-align: center;
    margin-bottom: 32px;
  `)}
`;

export const Timeline = styled.ol`
  list-style-type: none;
  padding-left: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0;

  ${applyStylesForScreenSize(tabletAndUp, css`
    flex-direction: row;
    justify-content: space-between;
    gap: 16px;
  `)}
`;

export const TimelineItem = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;

  ${applyStylesForScreenSize(tabletAndUp, css`
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;
  `)}

  /* Horizontal line through number circles (desktop only) */
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    display: none;
    ${applyStylesForScreenSize(tabletAndUp, css`
      display: block;
      left: calc(50% + 16px);
      top: calc(100px + 8px + 14px);
      width: 100%;
      height: 2px;
      background: ${SharedStyles.COLOR.BORDER.DEFAULT.DEFAULT};
      z-index: 0;
    `)}
  }
  /* Hide the vertical line on the last item */
  &:last-child > div:last-child {
    padding-bottom: 0;
  }

  &:last-child > div:last-child > span:first-child::after {
    display: none;
  }
`;

export const TimelineImageWrapper = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  ${applyStylesForScreenSize(tabletAndUp, css`
    width: 100px;
    height: 80px;
    margin-bottom: 8px;
  `)}
`;

export const TimelineImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

export const TimelineContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex: 1;
  padding-bottom: 24px;

  ${applyStylesForScreenSize(tabletAndUp, css`
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding-bottom: 0;
  `)}
`;

export const TimelineNumber = styled.span`
  ${TYPOGRAPHY.CIRCULAR.bold};
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background: ${SharedStyles.COLOR.SURFACE.SECONDARY.LIGHT};
  color: ${colors.blue};
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-bottom: 2px;
  aspect-ratio: 1/1;
  position: relative;
  z-index: 1;

  /* Vertical line connecting to next item (mobile only) */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: calc(100% + 36px);
    background: ${SharedStyles.COLOR.BORDER.DEFAULT.DEFAULT};
    z-index: -1;
    ${applyStylesForScreenSize(tabletAndUp, css`
      display: none;
    `)}
  }
`;

export const TimelineCopy = styled.p`
  ${TYPOGRAPHY.CIRCULAR.bold};
  font-size: 14px;
  color: ${colors.blue};
  margin: 0;
  line-height: 18px;
  text-align: left;

  ${applyStylesForScreenSize(tabletAndUp, css`
    text-align: center;
    max-width: 150px;
  `)}
`;

export const EligibilityCard = styled.div`
  background: ${SharedStyles.COLOR.SURFACE.SECONDARY.LIGHT};
  border-radius: 16px;
  border: 1px solid ${SharedStyles.COLOR.BORDER.SECONDARY.SUBTLE};
  padding: 16px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

export const EligibilityTitle = styled.h4`
  ${TYPOGRAPHY.CIRCULAR.black};
  color: ${colors.blue};
  font-size: 18px;
  line-height: 24px;
  margin: 0;
  text-align: left;
`;

export const EligibilityList = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style-type: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  text-align: left;
`;

export const EligibilityItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: ${SharedStyles.COLOR.TEXT.DEFAULT.BODY};
  line-height: 1.5;
  text-align: left;
`;

export const Checkmark = styled(IconCheck)`
  color: ${colors.sage5};
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
`;

export const Footer = styled.div`
  margin-top: 16px;
  background: transparent;
  text-align: center;
  font-size: 12px;
  color: ${SharedStyles.COLOR.TEXT.DEFAULT.BODY};
  line-height: 1.5;

  ${applyStylesForScreenSize(tabletAndUp, css`
    font-size: 13px;
  `)}
`;

export const Link = styled.a`
  ${TYPOGRAPHY.CIRCULAR.bold};
  color: ${colors.sage6};
  text-decoration: none;

  &:hover,
  &:focus,
  &:active {
    text-decoration: underline;
    color: ${colors.sage6};
  }
`;
